import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Share2 } from 'lucide-react'
import { useDocumentStore, type SaveStatus } from '../stores/useDocumentStore'
import { ShareDialog } from './ShareDialog'

export function DocHeader() {
  const navigate = useNavigate()
  const doc = useDocumentStore((s) => s.doc)
  const saveStatus = useDocumentStore((s) => s.saveStatus)
  const setTitle = useDocumentStore((s) => s.setTitle)

  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(doc?.title ?? '')
  const [shareOpen, setShareOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (editingTitle) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editingTitle])

  if (!doc) return null

  const commit = async () => {
    setEditingTitle(false)
    const next = titleDraft.trim() || 'Untitled'
    if (next !== doc.title) await setTitle(next)
  }

  return (
    <header className="flex-shrink-0 h-12 border-b border-border flex items-center justify-between px-4 md:px-6">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <button
          onClick={() => navigate('/graph')}
          className="p-1 text-muted-foreground hover:text-foreground"
          title="Back to documents"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        {editingTitle ? (
          <input
            ref={inputRef}
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit()
              else if (e.key === 'Escape') {
                setTitleDraft(doc.title)
                setEditingTitle(false)
              }
            }}
            className="bg-transparent border-b border-foreground/30 text-[13px] outline-none font-medium tracking-tight py-0.5 min-w-0 flex-1 max-w-[320px]"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          />
        ) : (
          <button
            onClick={() => {
              setTitleDraft(doc.title)
              setEditingTitle(true)
            }}
            className="text-[13px] font-medium tracking-tight hover:text-foreground/70 truncate min-w-0"
            style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
          >
            {doc.title}
          </button>
        )}
        <SaveIndicator status={saveStatus} />
      </div>

      <div className="flex items-center gap-4 flex-shrink-0">
        <button
          onClick={() => setShareOpen(true)}
          className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground font-light"
          style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
        >
          <Share2 className="w-3 h-3" strokeWidth={1.5} />
          Share
        </button>
      </div>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} />
    </header>
  )
}

function SaveIndicator({ status }: { status: SaveStatus }) {
  const dotClass =
    status === 'saved'
      ? 'bg-green-500'
      : status === 'saving'
        ? 'bg-amber-500 animate-pulse'
        : status === 'error'
          ? 'bg-destructive'
          : 'bg-red-500'

  const label =
    status === 'saved'
      ? 'Saved'
      : status === 'saving'
        ? 'Saving…'
        : status === 'error'
          ? 'Error'
          : 'Unsaved'

  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotClass}`} />
      <span
        className="text-[10px] uppercase tracking-wider text-muted-foreground font-light"
        style={{ fontFamily: 'Atlas Grotesk, system-ui, sans-serif' }}
      >
        {label}
      </span>
    </div>
  )
}
